// @flow
/** @jsx jsx */

import * as React from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { Mutation } from 'react-apollo';
import { jsx } from '@emotion/core';

import type { CommonActionProps } from './types.actions';
import type { ActionStatus } from '../../types';

import ToggleController from '../../controllers/ToggleController';
import StatusController from '../../controllers/StatusController';
import ButtonAction from './ButtonAction';
import Prompt from '../Prompt';
import Loading from '../Loading';
import { removeOrderMutation } from '../../graphql/mutations/orders/removeOrder.mutation';
import getMenuItems from '../../graphql/queries/getMenuItems.queries';
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query';
import messages from '../../config/messages';
import { HistoryContext } from '../../context/history.context';
import { AnnouncementsContext } from '../../context/announcements.context';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type Props = {
  onOrderRemoved: ActionStatus => typeof undefined,
};

type State = {
  deleting: boolean,
};

class RemoveOrderAction extends React.Component<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
  };

  state = {
    deleting: false,
  };

  deletingTimerId: TimeoutID;

  announcementsContext = this.context;

  onMutationCompleted = ({ data, history }: { data: any, history: any }) => {
    if (data.deleteOrder && data.deleteOrder.error) {
      return;
    }

    if (this.deletingTimerId) {
      clearTimeout(this.deletingTimerId);
    }

    this.setState({ deleting: false })

    this.props.onOrderRemoved('completed');

    this.announcementsContext.setMessage({
      key: 'anouncements',
      data: [
        {
          moduleName: 'removeOrder',
          anouncements: ['success'],
        },
      ],
    });

    history.push('/accounts');
  };

  componentWillUnmount = () => {
    if (this.deletingTimerId) {
      clearTimeout(this.deletingTimerId);
    }
  };

  onStartRemoving = ({ mutation, onToggleVisibility }: any) => async () => {
    this.setState({
      deleting: true,
    });

    try {
      await mutation();

      if (typeof onToggleVisibility === 'function') {
        onToggleVisibility();
      }
    } catch (e) {
      // TODO: handle error
    }
  };

  onExit = (options: { onToggleVisibility: () => mixed, loading: boolean }) => {
    const { onToggleVisibility, loading } = options;

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return;

    onToggleVisibility();
  };

  onSubmit = () => { };

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => {
          return (
            <HistoryContext.Consumer>
              {history => {
                return (
                  <Mutation
                    mutation={removeOrderMutation}
                    onCompleted={data =>
                      this.onMutationCompleted({ data, history })
                    }
                    refetchQueries={() => {
                      return [
                        {
                          query: getMenuItems,
                        },
                        {
                          query: getActiveOrderQuery
                        }
                      ];
                    }}
                    skip={!visible}
                  >
                    {(mutation, { loading, error }) => {
                      return (
                        <StatusController
                          hasError={!!error}
                          loading={loading}
                          delay={3000}
                        >
                          {({ status }) => {
                            return (
                              <React.Fragment>
                                <DesktopView>
                                  <ButtonAction
                                    bsSize={this.props.size}
                                    disabled={
                                      this.props.disabled ||
                                      loading ||
                                      this.state.deleting
                                    }
                                    onClick={onToggleVisibility}
                                  >

                                    <Glyphicon glyph="remove" />{' '}
                                    <span>
                                      {(status === '' || status === 'error') &&
                                        'Delete Order'}
                                      {status === 'loading' &&
                                        'Deleting order...'}
                                      {status === 'completed' && 'Order Deleted!'}
                                    </span>
                                  </ButtonAction>
                                </DesktopView>
                                <MobileView>
                                  <button disabled={
                                    this.props.disabled ||
                                    loading ||
                                    this.state.deleting
                                  }
                                    onClick={onToggleVisibility}
                                  >
                                    <img src='assets/bin.svg' alt='delete icon'/> Delete Order
                                  </button>
                                </MobileView>
                                {visible && (
                                  <MobileView>
                                    {(isMobile) => (
                                      <Modal
                                        show={visible}
                                        onHide={() =>
                                          this.onExit({
                                            onToggleVisibility,
                                            loading,
                                          })
                                        }
                                        className={'modal--form'}
                                        dialogClassName={isMobile ? 'confirmation-modal' : ''}
                                      >
                                        {!isMobile && 
                                          <Modal.Header closeButton={!isMobile}>
                                            <Modal.Title>Remove Order</Modal.Title>
                                          </Modal.Header>
                                        }
                                        <Modal.Body
                                          css={{
                                            minHeight: '300px',
                                          }}
                                        >
                                          {loading && <Loading />}
                                          <Prompt
                                            onCancel={() =>
                                              this.onExit({
                                                onToggleVisibility,
                                                loading,
                                              })
                                            }
                                            onConfirm={this.onStartRemoving({
                                              onToggleVisibility,
                                              mutation,
                                            })}
                                            message={messages.removeOrder.prompt}
                                            errorMessage={
                                              status === 'error'
                                                ? messages.removeOrder.fail
                                                : ''
                                            }
                                          />
                                        </Modal.Body>
                                      </Modal>
                                    )}
                                  </MobileView>
                                )}
                              </React.Fragment>
                            );
                          }}
                        </StatusController>
                      );
                    }}
                  </Mutation>
                );
              }}
            </HistoryContext.Consumer>
          );
        }}
      </ToggleController>
    );
  }
}

RemoveOrderAction.contextType = AnnouncementsContext;

export default RemoveOrderAction;
